// src/pages/admin/AdminDashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { coreApi, unwrap } from '../../services/api';
import { Card, Badge } from '../../components/ui';
import { ErrorBox, Skeleton } from '../../components/common/ErrorBoundary';

const STATUS_BADGE = {
  pending:'amber', confirmed:'blue', processing:'purple',
  shipped:'indigo', delivered:'green', cancelled:'red',
};

const STATUS_BAR_COLOR = {
  pending:    'bg-amber-400',
  confirmed:  'bg-blue-500',
  processing: 'bg-purple-500',
  shipped:    'bg-indigo-500',
  delivered:  'bg-green-500',
  cancelled:  'bg-red-400',
};

function DashboardSkeleton() {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_,i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
            <Skeleton className="h-3 w-24 mb-3"/>
            <Skeleton className="h-8 w-20 mb-2"/>
            <Skeleton className="h-3 w-32"/>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl h-64"><Skeleton className="h-full rounded-2xl"/></div>
        <div className="bg-white rounded-2xl h-64"><Skeleton className="h-full rounded-2xl"/></div>
      </div>
    </>
  );
}

/* ── Clean KPI card — simple bold number font ── */
function KpiCard({ icon, label, value, sub, accentColor, subColor }) {
  return (
    <div className={`bg-white rounded-2xl border-l-4 ${accentColor} border border-gray-100
      p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)]
      transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.1)]`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{label}</p>
          {/* FIX 3: Plus Jakarta Sans — simple, bold, clean (not Playfair serif) */}
          <p className="text-3xl font-black text-gray-900 leading-none mb-1.5"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', letterSpacing: '-0.02em' }}>
            {value}
          </p>
          {sub && (
            <p className={`text-xs font-semibold mt-1 ${subColor || 'text-gray-400'}`}>{sub}</p>
          )}
        </div>
        <div className="text-3xl opacity-60 flex-shrink-0">{icon}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const fetchStats = useCallback(() => {
    setLoading(true); setError(null);
    coreApi.dashboard()
      .then(res => { setStats(unwrap(res)); setError(null); })
      .catch(err => { setError(err); setStats(null); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const { products, orders, coupons, users, recent_orders } = stats || {};
  const revenue = parseFloat(orders?.revenue || 0);

  // FIX 2: order breakdown uses actual per-status counts from backend
  const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled'];
  const maxCount = Math.max(1, ...STATUSES.map(s => orders?.[s] ?? 0));

  return (
    <AdminLayout title="📊 Dashboard">
      {loading && <DashboardSkeleton />}
      {!loading && error && <ErrorBox error={error} onRetry={fetchStats} className="mb-4"/>}
      {!loading && stats && (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">

            <KpiCard
              icon="🎆" label="Active Products"
              value={products?.total ?? 0}
              sub={
                products?.out_of_stock > 0
                  ? `${products.out_of_stock} out of stock`
                  : products?.low_stock > 0
                  ? `${products.low_stock} running low`
                  : 'All in stock ✓'
              }
              subColor={products?.out_of_stock > 0 ? 'text-red-500' : products?.low_stock > 0 ? 'text-amber-500' : 'text-green-500'}
              accentColor="border-red-500"
            />

            <KpiCard
              icon="📦" label="Total Orders"
              value={orders?.total ?? 0}
              sub={`${orders?.pending ?? 0} pending`}
              accentColor="border-blue-500"
            />

            <KpiCard
              icon="💰" label="Revenue"
              value={`₹${revenue >= 100000 ? (revenue / 100000).toFixed(1) + 'L' : revenue.toLocaleString('en-IN')}`}
              sub="Excl. cancelled"
              accentColor="border-green-500"
            />

            <KpiCard
              icon="👥" label="Registered Users"
              value={users?.total ?? 0}
              sub={`${coupons?.active ?? 0} active coupons`}
              accentColor="border-amber-500"
            />
          </div>

          {/* ── FIX 4: Stock Alert — redesigned, clean, informative ── */}
          {(products?.out_of_stock > 0 || products?.low_stock > 0) && (
            <div className="mb-6 rounded-2xl overflow-hidden border border-amber-200">
              {/* Header bar */}
              <div className="bg-amber-50 px-5 py-3 flex items-center justify-between border-b border-amber-200/60">
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">⚠️</span>
                  <p className="font-black text-amber-800 text-sm">
                    Stock Alert
                  </p>
                  <div className="flex items-center gap-2 ml-1">
                    {products.out_of_stock > 0 && (
                      <span className="bg-red-100 text-red-700 font-black text-xs px-2.5 py-0.5 rounded-full border border-red-200">
                        {products.out_of_stock} out of stock
                      </span>
                    )}
                    {products.low_stock > 0 && (
                      <span className="bg-amber-100 text-amber-700 font-black text-xs px-2.5 py-0.5 rounded-full border border-amber-200">
                        {products.low_stock} running low
                      </span>
                    )}
                  </div>
                </div>
                <Link to="/admin/products"
                  className="text-amber-700 font-black text-xs hover:text-amber-900 transition-colors flex items-center gap-1">
                  Manage Products →
                </Link>
              </div>

              {/* Product list */}
              {products?.low_stock_items?.length > 0 && (
                <div className="bg-white px-5 py-3">
                  <div className="flex flex-wrap gap-2">
                    {products.low_stock_items.map(p => (
                      <div key={p.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold
                          ${p.stock === 0
                            ? 'bg-red-50 border-red-200 text-red-700'
                            : 'bg-amber-50 border-amber-200 text-amber-800'
                          }`}>
                        <span>{p.stock === 0 ? '🔴' : '🟡'}</span>
                        <span className="font-bold">{p.name}</span>
                        <span className={`font-black px-1.5 py-0.5 rounded-md text-[10px]
                          ${p.stock === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                          {p.stock === 0 ? 'OUT' : `${p.stock} left`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Recent Orders table */}
            <Card className="lg:col-span-2 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-black text-gray-800">Recent Orders</h2>
                <Link to="/admin/orders" className="text-xs text-red-600 font-bold hover:underline">
                  View All →
                </Link>
              </div>
              {!recent_orders?.length ? (
                <div className="py-12 text-center text-gray-400 text-sm">No orders yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 text-[11px] uppercase tracking-widest">
                        <th className="px-5 py-3 text-left">ID</th>
                        <th className="px-5 py-3 text-left">Customer</th>
                        <th className="px-5 py-3 text-left">Total</th>
                        <th className="px-5 py-3 text-left">Status</th>
                        <th className="px-5 py-3 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {recent_orders.map(o => {
                        const color = STATUS_BADGE[o.status] || 'gray';
                        return (
                          <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3.5 font-bold text-gray-600">#{o.id}</td>
                            <td className="px-5 py-3.5 text-gray-700 font-medium">{o.name}</td>
                            <td className="px-5 py-3.5 font-black text-gray-900"
                              style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                              ₹{parseFloat(o.total).toFixed(0)}
                            </td>
                            <td className="px-5 py-3.5">
                              <Badge color={color}>{o.status}</Badge>
                            </td>
                            <td className="px-5 py-3.5 text-gray-400 text-xs">
                              {new Date(o.created_at).toLocaleDateString('en-IN')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* FIX 2: Order Breakdown — uses actual per-status counts, bars scaled to max */}
            <Card className="p-5">
              <h3 className="font-black text-gray-800 mb-5">Order Breakdown</h3>
              <div className="space-y-4">
                {STATUSES.map(s => {
                  const count    = orders?.[s] ?? 0;
                  const pct      = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
                  const barColor = STATUS_BAR_COLOR[s] || 'bg-gray-300';
                  return (
                    <div key={s}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-600 capitalize">{s}</span>
                        <span className="text-sm font-black text-gray-800"
                          style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                          {count}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${barColor}`}
                          style={{ width: count === 0 ? '0%' : `${Math.max(pct, 4)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary total */}
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Orders</span>
                <span className="font-black text-gray-900 text-lg"
                  style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                  {orders?.total ?? 0}
                </span>
              </div>
            </Card>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
